'use client';

import Link from 'next/link';
import {
  Bell,
  BookOpenCheck,
  ClipboardList,
  Flame,
  Sparkles,
  Trophy,
  X,
} from 'lucide-react';
import type { LearningStreak } from '@/app/track/utils/streakTypes';
import type { LearningXp } from '@/app/track/utils/xpTypes';

export type NotificationItem = {
  id: string;
  title: string;
  message: string;
  timeLabel: string;
  href?: string;
  unread?: boolean;
  tone: 'orange' | 'violet' | 'emerald' | 'sky' | 'gray';
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  streak: LearningStreak;
  xp: LearningXp;
  readIds: string[];
  onRead: (id: string) => void;
};

function buildNotifications(streak: LearningStreak, xp: LearningXp): NotificationItem[] {
  const items: NotificationItem[] = [];

  if (streak.currentStreak > 0 && !streak.todayCompleted) {
    items.push({
      id: 'streak-reminder',
      title: 'Jaga streak-mu',
      message: `Kamu punya streak ${streak.currentStreak} hari. Selesaikan minimal 1 aktivitas hari ini.`,
      timeLabel: 'Hari ini',
      href: '/lesson',
      unread: true,
      tone: 'orange',
    });
  } else if (streak.todayCompleted) {
    items.push({
      id: 'streak-done',
      title: 'Streak hari ini tercatat',
      message: `Bagus! Streak ${streak.currentStreak} hari kamu aman untuk hari ini.`,
      timeLabel: 'Hari ini',
      href: '/track',
      tone: 'orange',
    });
  } else {
    items.push({
      id: 'streak-start',
      title: 'Mulai streak belajar',
      message: 'Kerjakan 1 soal, lesson, flashcard, atau ujian untuk memulai streak harian.',
      timeLabel: 'Tips',
      href: '/lesson',
      unread: true,
      tone: 'orange',
    });
  }

  if (xp.totalXp > 0) {
    items.push({
      id: 'xp-total',
      title: `Kamu punya ${xp.totalXp.toLocaleString('id-ID')} XP`,
      message: 'Lanjutkan belajar untuk menambah XP dari lesson, flashcard, dan bank soal.',
      timeLabel: 'Progres',
      href: '/track',
      tone: 'violet',
    });
  } else {
    items.push({
      id: 'xp-start',
      title: 'Kumpulkan XP pertamamu',
      message: 'Dapat +1 XP tiap jawaban benar, +10 XP saat menyelesaikan sublesson, dan bonus lainnya.',
      timeLabel: 'Tips',
      href: '/question-bank',
      unread: true,
      tone: 'violet',
    });
  }

  items.push(
    {
      id: 'lesson-tip',
      title: 'Lanjutkan pelajaran',
      message: 'Buka lesson dan klik Next di setiap step untuk XP dan streak.',
      timeLabel: 'Saran',
      href: '/lesson',
      tone: 'emerald',
    },
    {
      id: 'qb-tip',
      title: 'Latihan bank soal',
      message: 'Jawaban benar memberi +1 XP. Selesaikan set soal untuk bonus tambahan.',
      timeLabel: 'Saran',
      href: '/question-bank',
      tone: 'sky',
    }
  );

  if (streak.longestStreak > 0) {
    items.push({
      id: 'streak-record',
      title: 'Rekor streak kamu',
      message: `Streak terpanjang: ${streak.longestStreak} hari. Coba pecahkan rekor ini!`,
      timeLabel: 'Statistik',
      href: '/track',
      tone: 'gray',
    });
  }

  return items;
}

const toneStyles = {
  orange: 'bg-orange-50 text-orange-600 ring-orange-100',
  violet: 'bg-violet-50 text-violet-600 ring-violet-100',
  emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
  sky: 'bg-sky-50 text-sky-600 ring-sky-100',
  gray: 'bg-gray-50 text-gray-600 ring-gray-100',
} as const;

function NotificationIcon({ tone }: { tone: NotificationItem['tone'] }) {
  const className = 'h-5 w-5';
  if (tone === 'orange') return <Flame className={className} />;
  if (tone === 'violet') return <Sparkles className={className} />;
  if (tone === 'emerald') return <BookOpenCheck className={className} />;
  if (tone === 'sky') return <ClipboardList className={className} />;
  return <Trophy className={className} />;
}

export default function NotificationModal({ isOpen, onClose, streak, xp, readIds, onRead }: Props) {
  if (!isOpen) return null;

  const rawNotifications = buildNotifications(streak, xp);
  const notifications = rawNotifications.map((item) => ({
    ...item,
    unread: item.unread && !readIds.includes(item.id),
  }));
  const unreadCount = notifications.filter((item) => item.unread).length;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-slate-900/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-white shadow-2xl sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-modal-title"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-purple-100 text-purple-600">
              <Bell className="h-5 w-5" />
            </span>
            <div>
              <h2 id="notification-modal-title" className="text-lg font-bold text-gray-900">
                Notifikasi
              </h2>
              <p className="text-xs text-gray-500">
                {unreadCount > 0 ? `${unreadCount} belum dibaca` : 'Semua sudah dibaca'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Tutup notifikasi"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <ul className="divide-y divide-gray-100 overflow-y-auto">
          {notifications.map((item) => {
            const content = (
              <>
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${toneStyles[item.tone]}`}
                >
                  <NotificationIcon tone={item.tone} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                    {item.unread && (
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-rose-500" aria-hidden />
                    )}
                  </div>
                  <p className="mt-0.5 text-sm leading-relaxed text-gray-600">{item.message}</p>
                  <p className="mt-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    {item.timeLabel}
                  </p>
                </div>
              </>
            );

            return (
              <li key={item.id}>
                {item.href ? (
                  <Link
                    href={item.href}
                    onClick={() => {
                      if (item.unread) onRead(item.id);
                      onClose();
                    }}
                    className="flex gap-3 px-5 py-4 transition hover:bg-gray-50"
                  >
                    {content}
                  </Link>
                ) : (
                  <div
                    className={`flex gap-3 px-5 py-4 ${item.unread ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (item.unread) onRead(item.id);
                    }}
                  >
                    {content}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
          <Link
            href="/track"
            onClick={onClose}
            className="block text-center text-sm font-semibold text-brand-purpleText transition hover:underline"
          >
            Lihat semua progres
          </Link>
        </div>
      </div>
    </div>
  );
}

export function countUnreadNotifications(streak: LearningStreak, xp: LearningXp, readIds: string[] = []) {
  return buildNotifications(streak, xp).filter((item) => item.unread && !readIds.includes(item.id)).length;
}
